import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { SectionTitle } from "../../components/SectionTitle";
import { LoadingContainer } from "../../components/LoadingContainer";
import { InfoBox } from "../../components/InfoBox";
import { Button } from "../../components/Button";
import { StyledInput } from "../../components/Input";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { OneTimeSecret } from "../../components/OneTimeSecret";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../../components/Table";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import type { StoreId, WebhookEndpointId } from "@KassaroAB/shared/types/ids";
import type {
  RestWebhookEndpoint,
  RestWebhookTestResult,
} from "@KassaroAB/shared/types/webhookEndpoint";
import {
  SUBSCRIBABLE_WEBHOOK_EVENT_TYPES,
  WebhookEventType,
} from "@KassaroAB/shared/types/enums/WebhookEventType";
import { WebhookEndpointStatus } from "@KassaroAB/shared/types/enums/WebhookEndpointStatus";
import {
  useCreateWebhookEndpointMutation,
  useDeleteWebhookEndpointMutation,
  useGetWebhookEndpointsQuery,
  useRotateWebhookSecretMutation,
  useSendWebhookTestMutation,
  useUpdateWebhookEndpointMutation,
} from "../../query/webhookEndpointsQuery";
import { formatDateTime } from "../../utils/dateUtils";
import {
  SettingsActions as Actions,
  SettingsChoice as Choice,
  SettingsField as Field,
  SettingsFieldset as Fieldset,
  SettingsMuted as Muted,
  SettingsSection as Section,
  StatusBadge as Badge,
  type StatusTone,
} from "./SettingsSection";

type SubscribableEvent = (typeof SUBSCRIBABLE_WEBHOOK_EVENT_TYPES)[number];

const STATUS_TONE: Record<WebhookEndpointStatus, StatusTone> = {
  [WebhookEndpointStatus.ACTIVE]: "good",
  [WebhookEndpointStatus.PAUSED]: "muted",
  [WebhookEndpointStatus.DISABLED]: "bad",
};

const EndpointBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
  padding: ${spacing.x2}px 0;
  border-top: 1px solid ${colors.border};
`;

const EndpointUrl = styled.span`
  font-weight: 600;
  word-break: break-all;
`;

export const WebhooksSettingsView = () => {
  const { t } = useTranslation();
  const storeId = useParams<{ storeId: StoreId }>().storeId ?? null;
  const { data: endpoints, isLoading } = useGetWebhookEndpointsQuery(storeId);
  const create = useCreateWebhookEndpointMutation(storeId);
  const update = useUpdateWebhookEndpointMutation(storeId);
  const remove = useDeleteWebhookEndpointMutation(storeId);
  const rotate = useRotateWebhookSecretMutation(storeId);
  const test = useSendWebhookTestMutation(storeId);
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [events, setEvents] = useState<SubscribableEvent[]>([
    WebhookEventType.PRODUCT_CREATED,
    WebhookEventType.PRODUCT_UPDATED,
    WebhookEventType.PRODUCT_DELETED,
  ]);
  const [secret, setSecret] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<
    Partial<Record<WebhookEndpointId, RestWebhookTestResult>>
  >({});
  const [openDeliveries, setOpenDeliveries] =
    useState<WebhookEndpointId | null>(null);
  const [pendingDelete, setPendingDelete] =
    useState<RestWebhookEndpoint | null>(null);

  const toggleEvent = (event: SubscribableEvent) =>
    setEvents((current) =>
      current.includes(event)
        ? current.filter((value) => value !== event)
        : [...current, event],
    );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    create.mutate(
      {
        url: url.trim(),
        description: description.trim() || null,
        events,
      },
      {
        onSuccess: (result) => {
          setSecret(result.secret);
          setUrl("");
          setDescription("");
        },
      },
    );
  };

  const sendTest = (endpointId: WebhookEndpointId) =>
    test.mutate(endpointId, {
      onSuccess: (result) =>
        setTestResults((current) => ({ ...current, [endpointId]: result })),
    });

  const confirmDelete = () => {
    if (pendingDelete) {
      remove.mutate(pendingDelete.id);
    }
    setPendingDelete(null);
  };

  const renderTestResult = (endpointId: WebhookEndpointId) => {
    const result = testResults[endpointId];
    if (!result) {
      return null;
    }
    if (result.success) {
      return (
        <InfoBox variant="success">
          {t("storeSettings.webhooks.testSucceeded", {
            status: result.statusCode,
          })}
        </InfoBox>
      );
    }
    return (
      <InfoBox variant="error">
        {t("storeSettings.webhooks.testFailed", {
          error: result.error ?? "",
        })}
      </InfoBox>
    );
  };

  const renderEndpoint = (endpoint: RestWebhookEndpoint) => {
    const isActive = endpoint.status === WebhookEndpointStatus.ACTIVE;
    const deliveries = endpoint.recentDeliveries ?? [];
    return (
      <EndpointBlock key={endpoint.id}>
        <span>
          <EndpointUrl>{endpoint.url}</EndpointUrl>
          <Badge $tone={STATUS_TONE[endpoint.status]}>
            {t(`storeSettings.webhooks.status.${endpoint.status}`)}
          </Badge>
        </span>
        {endpoint.description ? <Muted>{endpoint.description}</Muted> : null}
        <Muted>
          {endpoint.events
            .map((event) => t(`storeSettings.webhooks.events.${event}`))
            .join(", ")}
        </Muted>
        {endpoint.failingSince ? (
          <InfoBox variant="warning">
            {t("storeSettings.webhooks.failingSince", {
              date: formatDateTime(endpoint.failingSince),
            })}
          </InfoBox>
        ) : null}
        {renderTestResult(endpoint.id)}
        <Actions>
          <Button
            type="button"
            $small
            $variant="outlined"
            disabled={test.isPending}
            onClick={() => sendTest(endpoint.id)}
          >
            {t("storeSettings.webhooks.sendTest")}
          </Button>
          <Button
            type="button"
            $small
            $variant="outlined"
            disabled={update.isPending}
            onClick={() =>
              update.mutate({
                endpointId: endpoint.id,
                data: {
                  status: isActive
                    ? WebhookEndpointStatus.PAUSED
                    : WebhookEndpointStatus.ACTIVE,
                },
              })
            }
          >
            {isActive
              ? t("storeSettings.webhooks.pause")
              : t("storeSettings.webhooks.activate")}
          </Button>
          <Button
            type="button"
            $small
            $variant="outlined"
            disabled={rotate.isPending}
            onClick={() =>
              rotate.mutate(endpoint.id, {
                onSuccess: (result) => setSecret(result.secret),
              })
            }
          >
            {t("storeSettings.webhooks.rotateSecret")}
          </Button>
          <Button
            type="button"
            $small
            $variant="outlined"
            onClick={() =>
              setOpenDeliveries((current) =>
                current === endpoint.id ? null : endpoint.id,
              )
            }
          >
            {t("storeSettings.webhooks.deliveries", {
              count: deliveries.length,
            })}
          </Button>
          <Button
            type="button"
            $small
            $variant="outlined"
            $color="danger"
            onClick={() => setPendingDelete(endpoint)}
          >
            {t("common.delete")}
          </Button>
        </Actions>
        {openDeliveries === endpoint.id && deliveries.length > 0 ? (
          <Table minWidth="560px">
            <TableHeader>
              <tr>
                <TableHeaderCell>
                  {t("storeSettings.webhooks.deliveryTime")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("storeSettings.webhooks.deliveryEvent")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("storeSettings.webhooks.deliveryAttempt")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("storeSettings.webhooks.deliveryResult")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("storeSettings.webhooks.deliveryDuration")}
                </TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {deliveries.map((delivery) => (
                <TableRow key={`${delivery.eventId}-${delivery.attempt}`}>
                  <TableCell noWrap>
                    {formatDateTime(delivery.deliveredAt)}
                  </TableCell>
                  <TableCell>{delivery.eventType}</TableCell>
                  <TableCell>{delivery.attempt}</TableCell>
                  <TableCell>
                    {delivery.statusCode ?? delivery.error ?? "–"}
                  </TableCell>
                  <TableCell noWrap>{delivery.durationMs} ms</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </EndpointBlock>
    );
  };

  const renderEndpoints = () => {
    if (isLoading) {
      return <LoadingContainer message={t("storeSettings.loading")} />;
    }
    if (!endpoints || endpoints.length === 0) {
      return <Muted>{t("storeSettings.webhooks.empty")}</Muted>;
    }
    return <div>{endpoints.map(renderEndpoint)}</div>;
  };

  return (
    <>
      <Section as="section" aria-labelledby="webhooks-title">
        <div>
          <SectionTitle id="webhooks-title">
            {t("storeSettings.webhooks.title")}
          </SectionTitle>
          <Muted>{t("storeSettings.webhooks.description")}</Muted>
        </div>
        <form onSubmit={submit}>
          <Section as="div">
            <Field>
              {t("storeSettings.webhooks.url")}
              <StyledInput
                type="url"
                value={url}
                placeholder="https://"
                onChange={(event) => setUrl(event.target.value)}
                required
              />
            </Field>
            <Field>
              {t("storeSettings.webhooks.endpointDescription")}
              <StyledInput
                value={description}
                maxLength={500}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Field>
            <Fieldset>
              <legend>{t("storeSettings.webhooks.eventsLabel")}</legend>
              {SUBSCRIBABLE_WEBHOOK_EVENT_TYPES.map((event) => (
                <Choice key={event}>
                  <input
                    type="checkbox"
                    checked={events.includes(event)}
                    onChange={() => toggleEvent(event)}
                  />
                  {t(`storeSettings.webhooks.events.${event}`)}
                </Choice>
              ))}
            </Fieldset>
            {create.error ? (
              <InfoBox variant="error">{create.error.message}</InfoBox>
            ) : null}
            <Actions>
              <Button
                type="submit"
                disabled={
                  create.isPending || !url.trim() || events.length === 0
                }
              >
                {t("storeSettings.webhooks.add")}
              </Button>
            </Actions>
          </Section>
        </form>
        {secret ? (
          <OneTimeSecret
            value={secret}
            message={t("storeSettings.webhooks.secretShowOnce")}
            onDone={() => setSecret(null)}
          />
        ) : null}
        {renderEndpoints()}
      </Section>
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title={t("storeSettings.webhooks.deleteTitle")}
        message={t("storeSettings.webhooks.deleteMessage", {
          url: pendingDelete?.url ?? "",
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
};
